import type { ResultOf, VariablesOf } from '@graphql-typed-document-node/core';
import { print } from 'graphql';

import { graphql } from '~/shared/codegen';
import { EnumCourseLanguage, SortFindManyCategoryInput } from '~/shared/codegen/graphql';
import { fetchGraphQL } from '~/shared/helpers/fetch-graphql';
import type { Locale } from '~/universal/constants';

type FindManySubscriptionCategoriesQueryData = ResultOf<typeof FindManySubscriptionCategoriesQuery>;
type FindManySubscriptionCategoriesQueryVariables = VariablesOf<typeof FindManySubscriptionCategoriesQuery>;

export type SubscriptionCategoryItem = NonNullable<
  FindManySubscriptionCategoriesQueryData['findManyCategoryQuery']
>[number];

export const SUBSCRIPTION_CATEGORIES_LIMIT = 100;

export const FindManySubscriptionCategoriesQuery = graphql(/* GraphQL */ `
  query findManySubscriptionCategoriesQuery(
    $limit: Int!
    $filter: FilterFindManyCategoryInput
    $sort: SortFindManyCategoryInput
  ) {
    findManyCategoryQuery(limit: $limit, filter: $filter, sort: $sort) {
      _id
      name
      alias
      language
      isPublished
    }
  }
`);

const categoriesCache = new Map<string, readonly SubscriptionCategoryItem[]>();
const pendingCategoryRequests = new Map<string, Promise<readonly SubscriptionCategoryItem[]>>();

export const getSubscriptionCategoriesCacheKey = ({
  lng,
  categoriesUrl,
}: Readonly<{
  lng: Locale;
  categoriesUrl: string;
}>): string => {
  return `${lng}:${categoriesUrl}`;
};

const mapCategories = (
  data: FindManySubscriptionCategoriesQueryData | undefined,
): readonly SubscriptionCategoryItem[] => {
  return data?.findManyCategoryQuery ?? [];
};

const mapLocaleToCategoryLanguage = (lng: Locale): EnumCourseLanguage => {
  switch (lng) {
    case 'ua':
      return EnumCourseLanguage.Ua;
    case 'ru':
      return EnumCourseLanguage.Ru;
    case 'en':
      return EnumCourseLanguage.En;
    default:
      return EnumCourseLanguage.Ua;
  }
};

export const resetSubscriptionCategoriesCache = (): void => {
  categoriesCache.clear();
  pendingCategoryRequests.clear();
};

export const getCachedSubscriptionCategories = ({
  lng,
  categoriesUrl = '/api/graphql',
}: Readonly<{
  lng: Locale;
  categoriesUrl?: string;
}>): readonly SubscriptionCategoryItem[] | null => {
  const cacheKey = getSubscriptionCategoriesCacheKey({ lng, categoriesUrl });

  return categoriesCache.get(cacheKey) ?? null;
};

export const fetchSubscriptionCategories = async ({
  lng,
  categoriesUrl = '/api/graphql',
}: Readonly<{
  lng: Locale;
  categoriesUrl?: string;
}>): Promise<readonly SubscriptionCategoryItem[]> => {
  const cacheKey = getSubscriptionCategoriesCacheKey({ lng, categoriesUrl });
  const cachedValue = categoriesCache.get(cacheKey);

  if (cachedValue) {
    return cachedValue;
  }

  const pendingRequest = pendingCategoryRequests.get(cacheKey);

  if (pendingRequest) {
    return pendingRequest;
  }

  const language = mapLocaleToCategoryLanguage(lng);

  const request = fetchGraphQL<
    FindManySubscriptionCategoriesQueryData,
    FindManySubscriptionCategoriesQueryVariables,
    FindManySubscriptionCategoriesQueryData
  >({
    lng,
    query: print(FindManySubscriptionCategoriesQuery),
    variables: {
      limit: SUBSCRIPTION_CATEGORIES_LIMIT,
      filter: {
        isPublished: true,
        language,
      },
      sort: SortFindManyCategoryInput.IdAsc,
    },
  })
    .then((data) => {
      const nextValue = mapCategories(data);

      categoriesCache.set(cacheKey, nextValue);
      pendingCategoryRequests.delete(cacheKey);

      return nextValue;
    })
    .catch((error: unknown) => {
      pendingCategoryRequests.delete(cacheKey);
      throw error;
    });

  pendingCategoryRequests.set(cacheKey, request);

  return request;
};
